// @flow

import React, {useMemo, useState} from 'react';
import {makeStyles} from '@material-ui/styles';
import {Divider, Grid, Hidden, Typography} from '@material-ui/core';

import {colors} from '../../../../../assets/styles/colors';
import {useIsFeatureInTier} from '../../../../../utils/component';

import CustomButton from '../../../../../elements/CustomButton/CustomButton';

import type {Tax} from '../../../../../entities';
import type {Theme} from '@material-ui/core';
import CustomSelect from '../../../../../elements/CustomSelect/CustomSelect';

const useStyles = makeStyles((theme: Theme) => ({
  contentSection: {
    marginTop: theme.spacing(2),
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    padding: theme.spacing(2, 3, 3, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(3, 2, 2, 2),
    },
    background: 'white',
  },
  rowTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  sectionTitle: {
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
  },
  sectionDescription: {
    color: colors.grey700,
    fontSize: '0.875rem',
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  taxCriteria: {
    color: colors.grey800,
    fontSize: 14,
    fontWeight: 600,
  },
  taxDesc: {
    color: colors.grey800,
    fontSize: 14,
  },
  taxList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  gridContainer: {
    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(1),
    },
  },
  wrapBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
}));

type Props = {
  taxesApplied: Tax[],
  allTaxes: Tax[],
  onSaveTax: (tax: Tax) => void,
};

export default function TaxesSection({
  taxesApplied,
  allTaxes,
  onSaveTax,
}: Props) {
  const hasTaxesFeature = useIsFeatureInTier('TAXES');

  const [taxSelected, setTaxSelected] = useState();

  const classes = useStyles();

  const taxesMap = useMemo(() => {
    if (taxesApplied.length > 0) {
      const orderTaxes = taxesApplied.filter(t => t.taxCriteria === 'ORDER');
      const additionalTaxes = taxesApplied.filter(
        t => t.taxCriteria !== 'ORDER'
      );
      return {orderTaxes, additionalTaxes};
    }
  }, [taxesApplied]);

  const handleSaveTax = () => {
    if (!taxSelected) return;

    const tax = allTaxes.find(t => t.name === taxSelected);

    if (!tax) return;
    onSaveTax(tax);
  };

  if (!hasTaxesFeature || !taxesApplied.length) return null;
  if (!taxesMap) return null;

  return (
    <section className={classes.contentSection}>
      <div className={classes.rowTitle}>
        <Typography className={classes.sectionTitle}>Impuestos</Typography>

        <Hidden smDown>
          <CustomButton to="/configure/taxes">
            Ir a configuración de impuestos
          </CustomButton>
        </Hidden>
      </div>
      <Hidden smDown>
        <Divider className={classes.divider} />
      </Hidden>
      <Grid container spacing={1} className={classes.gridContainer}>
        {taxesMap.orderTaxes.length > 0 && (
          <Grid item xs={12} md={6}>
            <Typography className={classes.taxCriteria}>
              Toda la orden
            </Typography>
            {taxesMap.orderTaxes.map(el => (
              <div key={el.id} className={classes.taxList}>
                <Typography className={classes.taxDesc}>
                  {el.name} &nbsp; - &nbsp;
                  {parseFloat(Number(el.percentage) * 100).toFixed(0)} %
                </Typography>
              </div>
            ))}
          </Grid>
        )}

        {taxesMap.additionalTaxes.length > 0 && (
          <Grid item xs={12} md={6}>
            <Typography className={classes.taxCriteria}>
              Impuesto adicionales
            </Typography>
            {taxesMap.additionalTaxes.map(el => (
              <div key={el.id} className={classes.taxList}>
                <Typography className={classes.taxDesc}>
                  {el.name} &nbsp; - &nbsp;
                  {parseFloat(Number(el.percentage) * 100).toFixed(0)} %
                </Typography>
              </div>
            ))}
          </Grid>
        )}
      </Grid>
      <Grid container spacing={1} className={classes.gridContainer}>
        <Typography className={classes.taxCriteria}>
          Selecciona el impuesto que deseas aplicar
        </Typography>
        <br />
        <Typography className={classes.taxDesc}>
          Selecciona el impuesto que deseas aplicar a este producto. Si no
          seleccionas ninguno, se aplicará el impuesto por defecto.
        </Typography>
        <CustomSelect
          native
          fullWidth
          value={taxSelected}
          name="taxType"
          onChange={e => {
            setTaxSelected(e.target.value);
          }}
        >
          <option value="" hidden />
          {allTaxes.map((t, index) => (
            <option key={t.name + index} value={t.name}>
              {t.label}
            </option>
          ))}
        </CustomSelect>

        {taxSelected && (
          <CustomButton onClick={handleSaveTax}>Guardar impuesto</CustomButton>
        )}
      </Grid>
      <Hidden mdUp>
        <Divider className={classes.divider} />
        <div className={classes.wrapBtn}>
          <CustomButton to="/configure/taxes">
            Ir a configuración de impuestos
          </CustomButton>
        </div>
      </Hidden>
    </section>
  );
}
